import { Component, forwardRef, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { GeneralService, FileUploadParams, FileDeleteParams, ChampionFileType, MediaObject } from '@gs-2.0/core';

export interface MediaUploadValue {
  uuid: string;
  url?: string;
  filename?: string;
  type?: string;
}

@Component({
  selector: 'app-media-upload-control',
  standalone: false,
  templateUrl: './media-upload-control.component.html',
  styleUrls: ['./media-upload-control.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MediaUploadControlComponent),
      multi: true
    }
  ]
})
export class MediaUploadControlComponent implements ControlValueAccessor, OnInit {
  @Input() entityUuid: string = '';
  @Input() secondUuid: string = '';
  @Input() acceptedTypes: string = '.jpg,.jpeg,.bmp,.png,.jfif,.webp';
  @Input() acceptedTypesLabel: string = 'jpg, jpeg, bmp, png, jfif, webp';
  @Output() uploadStateChange = new EventEmitter<boolean>();

  selectedFile: File | null = null;
  selectedFileName: string = '';
  fileSize: string = '';
  filePreviewUrl: string = '';
  uploadedFileUuid: string = '';
  uploadedFileType: string = '';
  isUploading: boolean = false;
  isVideo: boolean = false;

  private onChange: (value: MediaUploadValue | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private generalService: GeneralService) {}

  ngOnInit(): void {}

  writeValue(value: MediaUploadValue | null): void {
    if (value && value.uuid) {
      this.uploadedFileUuid = value.uuid;
      this.selectedFileName = value.filename || 'Uploaded File';
      this.filePreviewUrl = value.url || '';

      // Create a mock File object for UI display
      this.isVideo = value.type?.startsWith('video/') || false;
      this.selectedFile = new File([], this.selectedFileName, {
        type: value.type || (this.isVideo ? 'video/mp4' : 'image/jpeg')
      });

      // Store the file type for deletion
      this.uploadedFileType = this.isVideo ? ChampionFileType.VIDEO : ChampionFileType.IMAGE;

      this.fileSize = '';
      this.isUploading = false;
    } else {
      this.resetFileState();
    }
  }

  registerOnChange(fn: (value: MediaUploadValue | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      this.selectedFileName = this.selectedFile.name;
      this.fileSize = this.formatFileSize(this.selectedFile.size);
      this.isVideo = this.selectedFile.type.startsWith('video/');

      // Create preview URL for image or video
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        this.filePreviewUrl = e.target?.result as string;
      };
      reader.readAsDataURL(this.selectedFile);

      // Upload file to server
      this.uploadFileToServer();
    }
  }

  onUploadClick(): void {
    const fileInput = document.getElementById('mediaFileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  onRemoveFile(): void {
    // If file was uploaded to server, delete it via API
    if (this.uploadedFileUuid && this.entityUuid && this.uploadedFileType) {
      const deleteParams: FileDeleteParams = {
        entityUuid: this.entityUuid,
        fileType: this.uploadedFileType,
        fileUuid: this.uploadedFileUuid
      };

      this.generalService.deleteFile(deleteParams).subscribe({
        next: () => {
          console.log('File deleted successfully');
          this.resetFileState();
          this.onChange(null);
          this.onTouched();
        },
        error: (error) => {
          console.error('Error deleting file:', error);
          // Still reset the UI even if delete fails
          this.resetFileState();
          this.onChange(null);
          this.onTouched();
        }
      });
    } else {
      // No uploaded file, just reset the UI
      this.resetFileState();
      this.onChange(null);
      this.onTouched();
    }
  }

  private resetFileState(): void {
    this.selectedFile = null;
    this.selectedFileName = '';
    this.fileSize = '';
    this.filePreviewUrl = '';
    this.uploadedFileUuid = '';
    this.uploadedFileType = '';
    this.isUploading = false;
    this.isVideo = false;
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  private uploadFileToServer(): void {
    if (!this.selectedFile || !this.entityUuid) return;

    this.isUploading = true;
    this.uploadStateChange.emit(true);

    // Determine file type based on MIME type
    const isVideo = this.selectedFile.type.startsWith('video/');
    const fileType = isVideo ? ChampionFileType.VIDEO : ChampionFileType.IMAGE;

    const uploadParams: FileUploadParams = {
      entityUuid: this.entityUuid,
      fileType: fileType,
      secondUuid: this.secondUuid || this.entityUuid,
      file: this.selectedFile
    };

    this.generalService.uploadFile(uploadParams).subscribe({
      next: (response) => {
        this.isUploading = false;
        this.uploadStateChange.emit(false);
        this.uploadedFileUuid = response.response.uuid;
        this.uploadedFileType = fileType;

        // Emit the uploaded file info
        const value: MediaUploadValue = {
          uuid: response.response.uuid,
          url: response.response.url,
          filename: response.response.fileName,
          type: response.response.mimeType
        };

        this.onChange(value);
        this.onTouched();

        console.log('File uploaded successfully:', response.response);
      },
      error: (error) => {
        this.isUploading = false;
        this.uploadStateChange.emit(false);
        console.error('Error uploading file:', error);
        this.resetFileState();
        this.onChange(null);
      }
    });
  }
}
